import type { Theme } from '@material-ui/core';
import { createStyles, Grid } from '@material-ui/core';
import { makeStyles } from '@material-ui/core/styles';
import { Auth } from 'aws-amplify';
import React, { useEffect } from 'react';
import type { RxDatabase } from 'rxdb';

import config from '../../config/appConfig';
import { useMst } from '../../mobx-models/Root';
import { AimsDatabaseCollections } from '../../models/models';
import { sentryCaptureException, useFileStorage } from '../../utilities';
import { useEventTracking } from '../../utilities/hooks/useEventTracking';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    root: {
      backgroundColor: theme.palette.background.paper,
      height: '100vh',
    },
  }),
);
interface Props {
  rxdb?: RxDatabase<AimsDatabaseCollections> | undefined;
}

const Logout: React.FC<Props> = ({ rxdb }: Props) => {
  const classes = useStyles();

  const { primaryContext } = useMst();
  const { sendCachedEvents } = useEventTracking();
  const { clearAttachmentStore, uploadFilesInCache } = useFileStorage();

  const signOutUser = async () => {
    try {
      await uploadFilesInCache();
      await sendCachedEvents();
    } catch (error) {
      console.error('🚀 ~ file: Logout.tsx ~ line 30 ~ signOutUser ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: Logout.tsx ~ line 35 ~ signOutUser ~');
    }
    //Clear rxDB on logout
    try {
      const dbs = await window.indexedDB.databases();
      dbs.forEach(db => {
        window.indexedDB.deleteDatabase(db.name as string);
      });
    } catch (error) {
      console.error('🚀 ~ file: Logout.tsx ~ line 41 ~ signOutUser ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: Logout.tsx ~ line 50 ~ signOutUser ~');
    }

    // Clear image cache on logout
    if ('serviceWorker' in navigator) {
      try {
        clearAttachmentStore();
        const cache = await caches.open(`s3-images`);
        const imageKeys = await cache.keys();
        await Promise.all(imageKeys.map(key => cache.delete(key)));
      } catch (error) {
        console.error('🚀 ~ file: Logout.tsx ~ line 56 ~ signOutUser ~ error', error); // eslint-disable-line no-console
        // console logs will be removed once sentry implementation is done
        sentryCaptureException(error, '~ file: Logout.tsx ~ line 63 ~ signoutuser ~');
      }
    }

    const globalLogout = localStorage.getItem('globalLogout') === 'true';
    try {
      const userPoolWebClientId = config.aws_user_pools_web_client_id;
      const { domain, redirectSignIn } = config.Auth.oauth;
      if (localStorage.getItem('amplify-signin-with-hostedUI') === 'true') {
        window.location.href = `https://${domain}/logout?response_type=code&client_id=${userPoolWebClientId}&redirect_uri=${redirectSignIn}&state=STATE&scope=email+openid+profile`;
      } else {
        await Auth.signOut({
          global: globalLogout,
        });
        window.location.replace('/login'); // dont use history in this case a trigger a page refresh
      }
    } catch (error) {
      console.log('🚀 ~ file: Logout.tsx ~ line 30 ~ signOutUser ~ error', error); // eslint-disable-line no-console
      // console logs will be removed once sentry implementation is done
      sentryCaptureException(error, '~ file: Logout.tsx ~ line 81 ~ signOutUser ~');
    }
    sessionStorage.clear();
    localStorage.clear();
    primaryContext.resetStore();
  };

  useEffect(() => {
    signOutUser();
  }, [rxdb]);

  return (
    <Grid
      data-testid="logout"
      container
      className={classes.root}
      justifyContent="center"
      alignItems="center"
    >
      <Grid item xs={3} />
    </Grid>
  );
};

export default Logout;
